import { VISION } from '@sd/content';
import type { GameState } from './game';
import { hexSpiral, type Hex } from './hex';
import { techMods } from './techs';
import { eyesOf, visionInput } from './visibility';

/**
 * The classic fog of war (#97, the author's decision of 21.10.2026): a
 * player knows only the part of the system it has seen at least once.
 * There the map stays drawn — planets, dust clouds, outposts, a rival's
 * capital — while fleets show only where it has eyes now (visibility.ts).
 * Every player's explored hexes are a bitset over the map, hex by hex in
 * rows; allies explore together, as they see together.
 */
export type Explored = Readonly<Record<string, readonly number[]>>;

/** Index of each hex of a map of radius R, row by row from r = −R. */
function rows(radius: number): { offsets: number[]; count: number } {
  const offsets: number[] = [];
  let count = 0;
  for (let r = -radius; r <= radius; r++) {
    offsets.push(count);
    count += 2 * radius + 1 - Math.abs(r);
  }
  return { offsets, count };
}
const layouts = new Map<number, { offsets: number[]; count: number }>();
const layout = (radius: number) => {
  let l = layouts.get(radius);
  if (!l) layouts.set(radius, (l = rows(radius)));
  return l;
};

/** The hex's bit, or −1 off the map. */
export function hexIndex(radius: number, h: Hex): number {
  const s = -h.q - h.r;
  if (Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(s)) > radius) return -1;
  const qMin = Math.max(-radius, -h.r - radius);
  return layout(radius).offsets[h.r + radius]! + (h.q - qMin);
}

/** Whether `player` has ever seen the hex (an ally's eyes count). */
export function isExplored(state: GameState, player: string, h: Hex): boolean {
  const bits = state.explored[player];
  const i = hexIndex(state.system.mapRadius, h);
  return !!bits && i >= 0 && ((bits[i >>> 5]! >>> (i & 31)) & 1) === 1;
}

/**
 * Adds what every player sees now to what it has explored. Runs every
 * `VISION.exploreEveryTicks` ticks (and on `force`): a fleet sees two hexes
 * around it, so a second's lag never skips a hex.
 */
export function explore(state: GameState, force = false): GameState {
  if (!force && state.tick % VISION.exploreEveryTicks !== 0) return state;
  const radius = state.system.mapRadius;
  const words = Math.ceil(layout(radius).count / 32);
  const input = visionInput(state);
  let explored: Record<string, readonly number[]> | null = null;
  for (const p of state.players) {
    const old = state.explored[p.id];
    const bits = old ? [...old] : new Array<number>(words).fill(0);
    let changed = !old;
    if (techMods(p.techs).revealMap) {
      // A full star chart: every hex at once.
      const all = layout(radius).count;
      for (let i = 0; i < all; i++) {
        if (((bits[i >>> 5]! >>> (i & 31)) & 1) === 0) {
          bits[i >>> 5] = bits[i >>> 5]! | (1 << (i & 31));
          changed = true;
        }
      }
    } else {
      for (const { hex, radius: r } of eyesOf(input, p.id)) {
        for (const h of hexSpiral(hex, r)) {
          const i = hexIndex(radius, h);
          if (i < 0 || ((bits[i >>> 5]! >>> (i & 31)) & 1) === 1) continue;
          bits[i >>> 5] = bits[i >>> 5]! | (1 << (i & 31));
          changed = true;
        }
      }
    }
    if (changed) (explored ??= { ...state.explored })[p.id] = bits;
  }
  return explored ? { ...state, explored } : state;
}

/** Every hex `player` has explored, as hex keys, for the client's map. */
export function exploredKeys(state: GameState, player: string): Set<string> {
  const out = new Set<string>();
  const radius = state.system.mapRadius;
  for (let r = -radius; r <= radius; r++) {
    for (let q = Math.max(-radius, -r - radius); q <= Math.min(radius, -r + radius); q++) {
      if (isExplored(state, player, { q, r })) out.add(`${q},${r}`);
    }
  }
  return out;
}
